// ============================================================================
// SOP filter bar — shared by both Kanban and List views.
//
// Filter dimensions (per spec):
//   * Market region (multi-select, includes "No market" for null rows)
//   * Stage (multi-select, canonical 7-stage order)
//   * Status (Active / Stalled / All)
//   * Has start date (Yes / No / Any)
//   * Search (matches account_name, facility_name, contractor_name)
//
// Multi-selects are rendered as chip toggles rather than native <select
// multiple>, both because the option list is small (≤7) and because chip
// toggles avoid the iOS multi-select picker, which is awful for AMs in the
// field. Same UX pattern Account Management uses for the map filters.
// ============================================================================

import {
  NEW_ACCOUNT_SOP_STAGES,
  NEW_ACCOUNT_SOP_STAGE_LABELS,
  type NewAccountSopStage,
} from '../../shared/types';

export interface SopFilterState {
  markets: string[]; // empty = no filter; '__none__' selects rows where market_region is NULL
  stages: NewAccountSopStage[]; // empty = no filter
  statusMode: 'active' | 'stalled' | 'all';
  hasStartDate: 'yes' | 'no' | 'any';
  search: string;
}

export const NO_MARKET_TOKEN = '__none__';

export const DEFAULT_FILTERS: SopFilterState = {
  markets: [],
  stages: [],
  statusMode: 'active',
  hasStartDate: 'any',
  search: '',
};

interface Props {
  filters: SopFilterState;
  onChange: (next: SopFilterState) => void;
  // Distinct market tokens present in the dataset (excluding null — caller
  // passes them in so we don't show empty chips for markets nobody has).
  availableMarkets: string[];
  // Live count for the result label ("Showing N of 74").
  visibleCount: number;
  totalCount: number;
}

export default function SopFilters({
  filters,
  onChange,
  availableMarkets,
  visibleCount,
  totalCount,
}: Props) {
  const toggleMarket = (m: string) => {
    const next = filters.markets.includes(m)
      ? filters.markets.filter((x) => x !== m)
      : [...filters.markets, m];
    onChange({ ...filters, markets: next });
  };

  const toggleStage = (s: NewAccountSopStage) => {
    const next = filters.stages.includes(s)
      ? filters.stages.filter((x) => x !== s)
      : [...filters.stages, s];
    onChange({ ...filters, stages: next });
  };

  const reset = () => onChange(DEFAULT_FILTERS);
  const isFiltered =
    filters.markets.length > 0 ||
    filters.stages.length > 0 ||
    filters.statusMode !== DEFAULT_FILTERS.statusMode ||
    filters.hasStartDate !== 'any' ||
    filters.search.length > 0;

  return (
    <div className="rounded-lg border border-tcs-border bg-tcs-raised p-3">
      {/* Search + status + start-date row — always visible. */}
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={filters.search}
          onChange={(e) => onChange({ ...filters, search: e.target.value })}
          placeholder="Search account, facility, contractor…"
          className="min-w-[14rem] flex-1 rounded-md border border-tcs-border px-3 py-1.5 text-sm placeholder:text-tcs-text-muted focus:border-tcs-text focus:outline-none"
        />

        {/* Status segmented control */}
        <Segmented
          value={filters.statusMode}
          onChange={(v) =>
            onChange({ ...filters, statusMode: v as SopFilterState['statusMode'] })
          }
          options={[
            { value: 'active', label: 'Active' },
            { value: 'stalled', label: 'Stalled' },
            { value: 'all', label: 'All' },
          ]}
          ariaLabel="Status filter"
        />

        {/* Has start date segmented */}
        <Segmented
          value={filters.hasStartDate}
          onChange={(v) =>
            onChange({
              ...filters,
              hasStartDate: v as SopFilterState['hasStartDate'],
            })
          }
          options={[
            { value: 'any', label: 'Any start' },
            { value: 'yes', label: 'Has start' },
            { value: 'no', label: 'No start' },
          ]}
          ariaLabel="Start date filter"
        />

        {isFiltered && (
          <button
            type="button"
            onClick={reset}
            className="rounded-md border border-tcs-border px-2 py-1 text-xs text-tcs-text-secondary hover:bg-tcs-bg-muted"
          >
            Reset
          </button>
        )}
      </div>

      {/* Stage chips — wraps to multiple rows on narrow viewports. */}
      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        <span className="mr-1 text-xs font-medium uppercase tracking-wide text-tcs-text-muted">
          Stage
        </span>
        {NEW_ACCOUNT_SOP_STAGES.map((s) => (
          <Chip
            key={s}
            active={filters.stages.includes(s)}
            onClick={() => toggleStage(s)}
            label={NEW_ACCOUNT_SOP_STAGE_LABELS[s]}
          />
        ))}
      </div>

      {/* Market chips — only shown when there are multiple to choose from. */}
      {(availableMarkets.length > 0 || true) && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <span className="mr-1 text-xs font-medium uppercase tracking-wide text-tcs-text-muted">
            Market
          </span>
          {availableMarkets.map((m) => (
            <Chip
              key={m}
              active={filters.markets.includes(m)}
              onClick={() => toggleMarket(m)}
              label={m}
            />
          ))}
          <Chip
            active={filters.markets.includes(NO_MARKET_TOKEN)}
            onClick={() => toggleMarket(NO_MARKET_TOKEN)}
            label="No market"
          />
        </div>
      )}

      {/* Result count — small, right-aligned, low-emphasis. */}
      <div className="mt-2 text-right text-[11px] text-tcs-text-muted">
        Showing <strong className="text-tcs-text-secondary">{visibleCount}</strong> of{' '}
        {totalCount}
      </div>
    </div>
  );
}

function Chip({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-2.5 py-0.5 text-xs transition ${
        active
          ? 'border-tcs-inverse bg-tcs-inverse text-tcs-inverse-ink'
          : 'border-tcs-border bg-tcs-raised text-tcs-text-secondary hover:bg-tcs-bg-muted'
      }`}
    >
      {label}
    </button>
  );
}

function Segmented<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
  ariaLabel: string;
}) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className="flex items-center gap-0.5 rounded-md border border-tcs-border bg-tcs-raised p-0.5 text-xs"
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={`rounded px-2.5 py-1 ${
            value === o.value
              ? 'bg-tcs-surface font-medium text-tcs-text'
              : 'text-tcs-text-secondary hover:text-tcs-text'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
